import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { errorMessage } from "@/lib/errors";
import { trackEvent } from "@/lib/analytics";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email.trim().toLowerCase(), password);
      trackEvent("login", { method: "email" });
      toast.success("Welcome back");
      // If profile isn't set up (no photos or missing display name), send them to
      // /profile to complete it — otherwise their /u/:id public profile is blank.
      if (user && !user.profile_complete) {
        nav("/profile?onboard=1");
      } else {
        nav("/discover");
      }
    } catch (err) {
      toast.error(errorMessage(err, "Sign in failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] grid md:grid-cols-2">
      <div className="hidden md:block bg-[#722f37] relative">
        <img
          src="https://images.pexels.com/photos/8087992/pexels-photo-8087992.jpeg?auto=compress&cs=tinysrgb&h=650&w=940"
          alt="couple"
          className="w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 flex items-end p-12">
          <div>
            <h2 className="font-serif-display text-white text-5xl leading-tight">Welcome back.</h2>
            <p className="text-white/80 text-lg mt-3">Your next chapter is waiting.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <span className="text-sm uppercase tracking-[0.28em] font-bold text-[#722f37]">Sign in</span>
          <h1 className="font-serif-display text-4xl md:text-5xl mt-4">Good to see you.</h1>
          <p className="mt-3 text-[#5c5c5c] text-lg">Sign in to continue browsing.</p>

          <div className="mt-8">
            <GoogleSignInButton />
            <div className="mt-6 flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-[#5c5c5c]">
              <span className="h-px flex-1 bg-[#e5e0d8]" />
              <span>or with email</span>
              <span className="h-px flex-1 bg-[#e5e0d8]" />
            </div>
          </div>

          <div className="mt-6 space-y-5">
            <div>
              <label className="text-sm font-medium">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full h-14 mt-2 px-4 rounded-xl border border-[#d6cfc0] focus:border-[#722f37] focus:ring-2 focus:ring-[#722f37]/20 outline-none text-lg bg-white"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full h-14 mt-2 px-4 rounded-xl border border-[#d6cfc0] focus:border-[#722f37] focus:ring-2 focus:ring-[#722f37]/20 outline-none text-lg bg-white"
                data-testid="login-password-input"
              />
            </div>
          </div>

          <button type="submit" disabled={loading} className="btn-primary mt-8 w-full" data-testid="login-submit-btn">
            {loading ? "Signing in…" : "Sign in"}
          </button>

          <div className="mt-6 text-sm text-[#5c5c5c]">
            New here?{" "}
            <Link to="/register" className="text-[#722f37] font-medium" data-testid="login-to-register-link">
              Create a free account
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
